import { useEffect, useMemo, useState } from 'react'
import { Button, Skeleton } from '../../../shared/ui'
import { fetchOutreachSettings, saveWorkspaceSetting } from '../../../lib/outreach/api'
import { parsePhraseList } from '../../../lib/outreach/merge'
import { isValidTimeZone } from '../../../lib/outreach/ramp'
import { toast } from '../../../lib/toast'
import { ErrorLine, Field, errorMessage, reportError } from './shared'

function supportedZones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf
    return fn ? fn('timeZone') : []
  } catch {
    return []
  }
}

export function BusinessTab({ readOnly }: { readOnly: boolean }) {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [timezone, setTimezone] = useState('UTC')
  const [phrases, setPhrases] = useState('')
  const [tzError, setTzError] = useState('')
  const [phrasesError, setPhrasesError] = useState('')
  const [busy, setBusy] = useState(false)
  const zones = useMemo(supportedZones, [])

  useEffect(() => {
    let live = true
    fetchOutreachSettings()
      .then((s) => {
        if (!live) return
        setTimezone(s.timezone)
        setPhrases(s.blockedPhrases.join('\n'))
      })
      .catch((e) => live && setLoadError(errorMessage(e)))
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [])

  const saveTimezone = async () => {
    setTzError('')
    const tz = timezone.trim()
    if (!isValidTimeZone(tz)) {
      setTzError('That is not a timezone name. Use a name like Europe/London.')
      return
    }
    setBusy(true)
    try {
      await saveWorkspaceSetting('timezone', tz)
      setTimezone(tz)
      toast('Timezone saved', 'success')
    } catch (e) {
      setTzError(reportError(e))
    } finally {
      setBusy(false)
    }
  }

  const savePhrases = async () => {
    setPhrasesError('')
    const list = parsePhraseList(phrases)
    setBusy(true)
    try {
      await saveWorkspaceSetting('blocked_phrases', list)
      setPhrases(list.join('\n'))
      toast('Blocked phrases saved', 'success')
    } catch (e) {
      setPhrasesError(reportError(e))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="os-card" aria-busy="true">
        <Skeleton width="40" />
        <Skeleton width="70" />
      </div>
    )
  }

  return (
    <div className="os-root">
      <ErrorLine>{loadError}</ErrorLine>
      <div className="os-card">
        <Field
          id="biz-tz"
          label="Timezone"
          hint='The timezone sets what "today" and send days mean for every program.'
          error={tzError}
        >
          <input
            id="biz-tz"
            className="settings-input"
            list="biz-tz-list"
            disabled={readOnly}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          />
          <datalist id="biz-tz-list">
            {zones.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </Field>
        {!readOnly && (
          <div className="os-bar">
            <Button disabled={busy} onClick={() => void saveTimezone()}>
              Save timezone
            </Button>
          </div>
        )}
      </div>

      <div className="os-card">
        <Field
          id="biz-phrases"
          label="Blocked phrases"
          hint="One per line. Programs also have their own list. The sequence editor flags any step that contains a phrase."
          error={phrasesError}
        >
          <textarea
            id="biz-phrases"
            className="settings-input"
            rows={6}
            disabled={readOnly}
            value={phrases}
            onChange={(e) => setPhrases(e.target.value)}
          />
        </Field>
        {!readOnly && (
          <div className="os-bar">
            <Button disabled={busy} onClick={() => void savePhrases()}>
              Save blocked phrases
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
